import React, { useState } from 'react';
import { useCreateFeedback } from '../functions/feedBackHook'; // Asegúrate de la ruta correcta del hook

const FeedbackPage: React.FC = () => {
  const [feedback, setFeedback] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [rating, setRating] = useState(5);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Usando el hook para crear un feedback
  const { mutate: createFeedback, isLoading, isSuccess, error } = useCreateFeedback();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const formErrors: { [key: string]: string } = {};
    if (!name) formErrors.name = 'El nombre es obligatorio.';
    if (!email) formErrors.email = 'El correo electrónico es obligatorio.';
    if (!feedback) formErrors.feedback = 'El comentario es obligatorio.';
    if (!agreedToTerms) formErrors.terms = 'Debes aceptar los términos y condiciones.';

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Crear el objeto para enviar
    const feedbackData = {
      name,
      email,
      telefone: phone,
      rating,
      comments: feedback,
      termsAccepted: agreedToTerms
    };

    try {
      console.log('Enviando feedback:', feedbackData);  // Aquí agregamos un console log para ver lo que estamos enviando
      await createFeedback(feedbackData);

      // Limpiar los campos del formulario después del envío exitoso
      setName('');
      setEmail('');
      setPhone('');
      setFeedback('');
      setRating(5);
      setAgreedToTerms(false);
      setErrors({}); // Limpiar los errores después del envío exitoso
    } catch (error: any) {
      console.error('Error al enviar el feedback:', error);
      
      // Mostrar el error específico del servidor
      const errorMessage = error?.response?.data?.message || 'Hubo un error al enviar el feedback.';
      setErrors({ general: errorMessage });
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback(e.target.value);
  };

  return (
    <div className="bg-gray-50 min-h-screen flex items-center justify-center p-2 lg:p-6">
      <div className="max-w-2xl w-full bg-white p-10 rounded-2xl shadow-xl">
        <h2 className="text-2xl font-bold text-navy-900 text-center mb-6">
          ¡Tu opinión es importante para nosotros!
        </h2>
        <p className="text-gray-600 text-center mb-8">
          Comparte tus comentarios para ayudarnos a mejorar tu experiencia.
        </p>

        {/* Mostrar mensaje de éxito si isSuccess es true */}
        {isSuccess && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded">
            ¡Gracias por tu feedback!
          </div>
        )}

        {/* Mostrar mensaje de error si hay un error general */}
        {errors.general && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded">
            {errors.general}
          </div>
        )}

        {/* Formulario de Feedback */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-navy-900 font-semibold text-sm">Nombre</label>
            <input
              type="text"
              id="name"
              className={`w-full mt-2 p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.name ? 'border-red-500' : ''}`}
              value={name}
              onChange={handleNameChange}
              placeholder="Tu nombre"
            />
            {errors.name && <span className="text-red-500 text-sm">{errors.name}</span>}
          </div>

          <div>
            <label htmlFor="email" className="block text-navy-900 font-semibold text-sm">Correo Electrónico</label>
            <input
              type="email"
              id="email"
              className={`w-full mt-2 p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.email ? 'border-red-500' : ''}`}
              value={email}
              onChange={handleEmailChange}
              placeholder="Tu correo electrónico"
            />
            {errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
          </div>

          <div>
            <label htmlFor="phone" className="block text-navy-900 font-semibold text-sm">Teléfono (Opcional)</label>
            <input
              type="tel"
              id="phone"
              className="w-full mt-2 p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="Tu teléfono"
            />
          </div>

          <div>
            <label htmlFor="rating" className="block text-navy-900 font-semibold text-sm">Calificación</label>
            <select
              id="rating"
              className="w-full mt-2 p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={rating}
              onChange={(e) => setRating(parseInt(e.target.value))}
            >
              <option value={5}>⭐⭐⭐⭐⭐ Excelente</option>
              <option value={4}>⭐⭐⭐⭐ Bueno</option>
              <option value={3}>⭐⭐⭐ Aceptable</option>
              <option value={2}>⭐⭐ Deficiente</option>
              <option value={1}>⭐ Muy deficiente</option>
            </select>
          </div>

          <div>
            <label htmlFor="feedback" className="block text-navy-900 font-semibold text-sm">Comentarios</label>
            <textarea
              id="feedback"
              className={`w-full mt-2 p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.feedback ? 'border-red-500' : ''}`}
              rows={4}
              value={feedback}
              onChange={handleFeedbackChange}
              placeholder="Escribe tus comentarios aquí..."
            />
            {errors.feedback && <span className="text-red-500 text-sm">{errors.feedback}</span>}
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="terms" className="text-sm text-navy-900">
              Acepto los <span className="text-indigo-600">términos y condiciones</span>
            </label>
            {errors.terms && <span className="text-red-500 text-sm">{errors.terms}</span>}
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              className={`w-full py-3 text-white font-semibold rounded-xl ${isLoading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'} transition-colors duration-300`}
              disabled={isLoading || !agreedToTerms}
            >
              {isLoading ? 'Enviando...' : 'Enviar Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackPage;
